/**
 * Environment Configuration Utility
 * 
 * Provides environment detection and dynamic configuration for OAuth and other
 * environment-specific settings in Emmy's Learning App.
 */
import { logger } from './logger';

/**
 * Detects if the application is running in development mode
 * @returns {boolean} True if in development, false if in production
 */
export const isDevelopment = () => {
  // Check for Vite development mode (safely handle import.meta.env)
  try {
    if (import.meta.env && import.meta.env.DEV) {
      return true;
    }
  } catch (error) {
    // import.meta.env not available (e.g., in Node.js testing)
  }
  
  // Check for localhost or development URLs
  const hostname = window.location.hostname;
  const isDev = hostname === 'localhost' || 
                hostname === '127.0.0.1' || 
                hostname.startsWith('192.168.') ||
                hostname.endsWith('.local');
  
  return isDev;
};

/**
 * Detects if the application is running in production mode
 * @returns {boolean} True if in production, false if in development
 */
export const isProduction = () => {
  return !isDevelopment();
};

/**
 * Gets the current environment name
 * @returns {string} 'development' or 'production'
 */
export const getEnvironment = () => {
  return isDevelopment() ? 'development' : 'production';
};

/**
 * Gets the base URL for the current environment
 * @returns {string} The base URL including protocol and domain
 */
export const getBaseUrl = () => {
  if (isDevelopment()) {
    // For local development, use the current origin
    return window.location.origin;
  } else {
    // For production, use the custom domain (emmyslearning.app)
    // Fallback to current origin if custom domain not detected
    const hostname = window.location.hostname;
    if (hostname === 'emmyslearning.app' || hostname === 'www.emmyslearning.app') {
      return `https://${hostname}`;
    }
    // Fallback to current origin (works for Vercel preview URLs too)
    return window.location.origin;
  }
};

/**
 * Gets the full application URL including the base path
 * @returns {string} The complete application URL
 */
export const getAppUrl = () => {
  const baseUrl = getBaseUrl();
  
  // Use root path for custom domain (emmyslearning.app), or base path from env
  const basePath = import.meta.env?.VITE_BASE_PATH || '/';
  
  if (isDevelopment()) {
    // Local development doesn't need the base path
    return baseUrl;
  } else {
    // Production: use base path (root for custom domain, or /emmys-learning-app/ for GitHub Pages)
    return basePath === '/' ? baseUrl : `${baseUrl}${basePath.replace(/\/$/, '')}`;
  }
};

/**
 * Gets OAuth configuration for the current environment
 * @returns {object} OAuth configuration object
 */
export const getOAuthConfig = () => {
  const appUrl = getAppUrl();
  
  // For HashRouter, we need to use hash-based routing
  // Supabase will append #access_token=... to the redirect URL
  // So we need: https://emmyslearning.app/#/auth/callback
  // But Supabase will make it: https://emmyslearning.app/#/auth/callback#access_token=...
  // The AuthCallback component handles this double-hash format
  const redirectTo = `${appUrl}/#/auth/callback`;
  
  return {
    // Dynamic redirect URL based on environment
    // Note: Supabase will append #access_token=... to this URL
    // For HashRouter, we need to use hash-based routing
    redirectTo,
    
    // OAuth query parameters
    queryParams: {
      access_type: 'offline',
      prompt: 'consent',
    },
    
    // Additional OAuth options
    options: {
      // Skip confirmation for development
      skipBrowserRedirect: false,
      
      // Provider-specific settings
      scopes: 'email profile',
    }
  };
};

/**
 * Gets environment-specific configuration
 * @returns {object} Configuration object for the current environment
 */
export const getEnvironmentConfig = () => {
  const env = getEnvironment();
  const baseUrl = getBaseUrl();
  const appUrl = getAppUrl();
  
  return {
    environment: env,
    isDevelopment: isDevelopment(),
    isProduction: isProduction(),
    baseUrl,
    appUrl,
    
    // OAuth configuration
    oauth: getOAuthConfig(),
    
    // API endpoints (if needed in the future)
    api: {
      baseUrl: appUrl,
    },
    
    // Feature flags based on environment
    features: {
      // Enable debug logging in development
      debugLogging: isDevelopment(),
      
      // Enable developer mode access
      developerMode: isDevelopment(),
      
      // Enable service worker in production
      serviceWorker: isProduction(),
    },
    
    // Environment-specific settings
    settings: {
      // Timeouts for authentication - increased to allow for profile loading
      authTimeout: isDevelopment() ? 15000 : 20000,
      
      // More verbose error messages in development
      verboseErrors: isDevelopment(),
      
      // Auto-refresh intervals
      sessionRefreshInterval: isDevelopment() ? 30000 : 60000, // 30s dev, 60s prod
      
      // Error recovery settings
      maxRetryAttempts: 3,
      retryDelayMs: 1000,
      exponentialBackoff: true,
      fallbackTimeout: 30000, // 30 seconds before showing fallback options
    }
  };
};

/**
 * Logs environment information to console (development only)
 */
export const logEnvironmentInfo = () => {
  if (!isDevelopment()) return;
  
  const config = getEnvironmentConfig();
  logger.group('🌍 Environment Configuration');
  logger.debug('Environment:', config.environment);
  logger.debug('Base URL:', config.baseUrl);
  logger.debug('App URL:', config.appUrl);
  logger.debug('OAuth Redirect:', config.oauth.redirectTo);
  logger.debug('Features:', config.features);
  logger.groupEnd();
};

// Export default configuration
export default getEnvironmentConfig();